import React, { useEffect, useState } from "react";
import "./LessonViewer.css";
import QuizEngine from "./QuizEngine.js";
import { getQuizForLesson } from "./quizData.js";
import { getLessonContent } from "./lessonContent.js";
import LessonVisual from "./LessonVisual.js";
import { useOptionalUserStorage } from "../storage/UserStorageContext";

function LessonViewer({ lesson, onBack }) {
  const authenticatedStorage = useOptionalUserStorage();
  const storage = authenticatedStorage || window.localStorage;
  const storageKey = `aninaAcademyLesson:${lesson?.id}`;
  const quizStorageKey = `aninaAcademyQuiz:${lesson?.id}`;

  const quiz = getQuizForLesson(lesson?.id);
  const lessonContent = getLessonContent(lesson?.id);

  const [showQuiz, setShowQuiz] = useState(false);

  const [isComplete, setIsComplete] = useState(() => {
    if (!lesson) {
      return false;
    }

    return storage.getItem(storageKey) === "complete";
  });

  const [quizResult, setQuizResult] = useState(() => {
    if (!lesson) {
      return null;
    }

    const savedResult = storage.getItem(quizStorageKey);

    if (!savedResult) {
      return null;
    }

    try {
      return JSON.parse(savedResult);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!lesson) {
      return;
    }

    setIsComplete(
      storage.getItem(
        `aninaAcademyLesson:${lesson.id}`
      ) === "complete"
    );

    const savedQuizResult = storage.getItem(
      `aninaAcademyQuiz:${lesson.id}`
    );

    if (!savedQuizResult) {
      setQuizResult(null);
      setShowQuiz(false);
      return;
    }

    try {
      setQuizResult(JSON.parse(savedQuizResult));
    } catch {
      setQuizResult(null);
    }

    setShowQuiz(false);
  }, [lesson, storage]);

  if (!lesson) {
    return null;
  }

  const displayedProgress = isComplete
    ? 100
    : lesson.progress || 0;

  const overview =
    lessonContent?.overview ||
    "This lesson explains the core idea in a practical way so you can recognize it during real market conditions.";

  const objectives =
    lessonContent?.objectives || [
      "Understand the main lesson concept.",
      "Recognize confirmation before entering a trade.",
      "Combine the setup with risk management.",
    ];

  const concepts =
    lessonContent?.concepts || [
      {
        title: "Understand the concept",
        text:
          "Study the idea before attempting to use it in a live trade.",
      },
      {
        title: "Wait for confirmation",
        text:
          "Avoid reacting to one signal without supporting evidence.",
      },
      {
        title: "Protect capital",
        text:
          "Combine every setup with a clear stop and trade plan.",
      },
    ];

  const exampleTitle =
    lessonContent?.exampleTitle || "Practical application";

  const example =
    lessonContent?.example ||
    "The AI Assistant will eventually connect this lesson to live chart examples, paper trades, and your personal learning history.";

  const ninaNote =
    lessonContent?.ninaNote ||
    "The AI Assistant should combine this lesson with market context, confirmation, and risk management.";

  const notifyProgressUpdated = () => {
    window.dispatchEvent(
      new Event("anina:academy-progress-updated")
    );
  };

  const handleCompleteLesson = () => {
    const nextCompleteState = !isComplete;

    setIsComplete(nextCompleteState);

    if (nextCompleteState) {
      storage.setItem(storageKey, "complete");
    } else {
      storage.removeItem(storageKey);
    }
    notifyProgressUpdated();
  };

  const handleQuizComplete = (result) => {
    setQuizResult(result);

    storage.setItem(
      quizStorageKey,
      JSON.stringify(result)
    );

    if (result.passed) {
      storage.setItem(storageKey, "complete");
      setIsComplete(true);
    }
    notifyProgressUpdated();
  };

  if (showQuiz && quiz) {
    return (
      <QuizEngine
        quiz={quiz}
        onBack={() => setShowQuiz(false)}
        onComplete={handleQuizComplete}
      />
    );
  }

  return (
    <section className="lessonViewer">
      <button
        type="button"
        className="lessonBackButton"
        onClick={onBack}
      >
        ← Back to Academy
      </button>

      <header className="lessonViewerHeader">
        <div>
          <span>{lesson.category}</span>

          <h1>{lesson.title}</h1>

          <p>{lesson.description}</p>
        </div>

        <div className="lessonMetaCard">
          <strong>{lesson.duration}</strong>
          <span>{lesson.level || "Beginner"}</span>
        </div>
      </header>

      <div className="lessonContentGrid">
        <article className="lessonMainContent">
          <section>
            <span className="lessonSectionLabel">
              LESSON OVERVIEW
            </span>

            <h2>What this lesson teaches</h2>

            <p>{overview}</p>
          </section>

          <section>
            <h2>Learning objectives</h2>

            <div className="lessonConceptList">
              {objectives.map((objective, index) => (
                <div key={objective}>
                  <strong>{index + 1}</strong>
                  <p>{objective}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2>Key concepts</h2>

            <div className="lessonDetailedConceptList">
              {concepts.map((concept, index) => (
                <div
                  className="lessonDetailedConcept"
                  key={concept.title}
                >
                  <span>{index + 1}</span>

                  <div>
                    <h3>{concept.title}</h3>
                    <p>{concept.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <LessonVisual lessonId={lesson.id} />

          <section>
            <h2>{exampleTitle}</h2>

            <div className="lessonExampleCard">
              <span>REAL-WORLD APPLICATION</span>
              <p>{example}</p>
            </div>
          </section>

          <section>
            <h2>AI guidance</h2>

            <div className="lessonNinaNote">
              <span>🤖</span>

              <div>
                <strong>How the AI Assistant should use this lesson</strong>
                <p>{ninaNote}</p>
              </div>
            </div>
          </section>
        </article>

        <aside className="lessonSidebar">
          <div className="lessonProgressPanel">
            <span>LESSON PROGRESS</span>

            <strong>
              {displayedProgress}% complete
            </strong>

            <div className="lessonViewerProgressTrack">
              <div
                className="lessonViewerProgressFill"
                style={{
                  width: `${displayedProgress}%`,
                }}
              />
            </div>
          </div>

          {quiz && (
            <div className="lessonQuizPanel">
              <span>KNOWLEDGE CHECK</span>

              <strong>
                {quizResult
                  ? `${quizResult.scorePercent}% score`
                  : "Quiz available"}
              </strong>

              <p>
                {quizResult?.passed
                  ? "You passed this lesson quiz."
                  : "Complete the quiz to test your understanding."}
              </p>

              <button
                type="button"
                className="lessonQuizButton"
                onClick={() => setShowQuiz(true)}
              >
                {quizResult
                  ? "Retake Quiz"
                  : "Take Quiz"}
              </button>
            </div>
          )}

          <button
            type="button"
            className="lessonCompleteButton"
            onClick={handleCompleteLesson}
          >
            {isComplete
              ? "✓ Lesson Complete"
              : "Mark Lesson Complete"}
          </button>
        </aside>
      </div>
    </section>
  );
}

export default LessonViewer;